import React, { useState, useEffect, useRef, useMemo } from 'react';
import { crmSnippetTemplates } from '../services/crmSnippetTemplates';

function today() { return new Date().toISOString().slice(0, 10); }

const FOLLOWUP_OPTIONS = [
  { label: 'In 1 Werktag', days: 1 },
  { label: 'In 3 Werktagen', days: 3 },
  { label: 'In 1 Woche', days: 7 },
  { label: 'In 2 Wochen', days: 14 },
];

function getWeekday(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('de-DE', { weekday: 'long' });
}

export default function FloatingNote({ lead, currentUser, onSave, onClose }) {
  const [text, setText]             = useState('');
  const [createTask, setCreateTask] = useState(false);
  const [taskType, setTaskType]     = useState('To-do');
  const [followupIdx, setFollowupIdx] = useState(1);
  const [pos, setPos]               = useState({ x: Math.max(60, window.innerWidth / 2 - 240), y: Math.max(60, window.innerHeight / 2 - 260) });
  const [minimized, setMinimized]   = useState(false);

  const noteSnippets = useMemo(() => {
    const pack = crmSnippetTemplates.get();
    const branch = (lead?.branch || '').toLowerCase();
    const status = (lead?.status || '').toLowerCase();
    const byB = (pack.noteByBranch || []).filter(n => !n.branch || branch.includes((n.branch || '').toLowerCase()));
    const byS = (pack.noteByStatus || []).filter(n => !n.status || status.includes((n.status || '').toLowerCase()));
    return [...byB, ...byS].slice(0, 8);
  }, [lead?.branch, lead?.status]);

  const dragging = useRef(false);
  const dragOff  = useRef({ x: 0, y: 0 });

  useEffect(() => {
    function onMove(e) {
      if (!dragging.current) return;
      setPos({ x: e.clientX - dragOff.current.x, y: e.clientY - dragOff.current.y });
    }
    function onUp() { dragging.current = false; }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, []);

  function startDrag(e) {
    if (e.target.closest('button') || e.target.closest('select')) return;
    dragging.current = true;
    dragOff.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.preventDefault();
  }

  function handleSave() {
    if (!text.trim()) return;
    const now = new Date();
    const dateStr  = now.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const timeStr  = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const monthStr = now.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    const dueD = new Date(); dueD.setDate(dueD.getDate() + FOLLOWUP_OPTIONS[followupIdx].days);
    const dueDateStr = dueD.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    onSave({
      id: Date.now(),
      type: createTask ? 'task' : 'note',
      month: monthStr,
      title: createTask ? `${taskType}: ${text.slice(0, 55)}${text.length > 55 ? '…' : ''}` : 'Notiz',
      by: currentUser?.name || 'Robin Obrecht',
      date: dateStr,
      time: timeStr,
      note: text,
      done: false,
      dueLabel: createTask ? `Fällig: ${dueDateStr}` : null,
    });
    onClose();
  }

  const fo = FOLLOWUP_OPTIONS[followupIdx];

  return (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 400,
      width: 480,
      background: 'var(--glass-modal)',
      backdropFilter: 'var(--glass-blur-lg)',
      WebkitBackdropFilter: 'var(--glass-blur-lg)',
      borderRadius: 16,
      border: '1px solid rgba(255,255,255,0.82)',
      boxShadow: '0 32px 80px rgba(15,40,100,0.22), 0 8px 24px rgba(15,40,100,0.12), 0 0 0 0.5px rgba(255,255,255,0.9), inset 0 1px 0 rgba(255,255,255,1)',
      overflow: 'hidden',
      fontFamily: 'var(--font)',
    }}>

      {/* Header */}
      <div onMouseDown={startDrag} style={{
        background: 'linear-gradient(135deg, rgba(30,58,140,0.88), rgba(29,190,116,0.80))',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        padding: '11px 14px',
        cursor: 'grab',
        userSelect: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        borderBottom: '1px solid rgba(255,255,255,0.18)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.85)" strokeWidth="1.8"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#fff', letterSpacing: '-0.01em' }}>Notiz</span>
        </div>
        {/* Drag dots center */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <svg width="20" height="10" viewBox="0 0 20 10" fill="rgba(255,255,255,0.45)">
            {[0,4,8,12,16].map(x => <circle key={x} cx={x+2} cy={5} r={1.5}/>)}
          </svg>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setMinimized(m => !m)} style={iconBtnStyle} title="Minimieren">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
          </button>
          <button style={iconBtnStyle} title="Maximieren">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
          </button>
          <button onClick={onClose} style={iconBtnStyle} title="Schließen">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.75)" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      {!minimized && (
        <div>
          {/* For chip */}
          <div style={{ padding: '10px 14px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Für</span>
            <span style={{
              fontSize: 12, fontWeight: 600, color: 'var(--accent)',
              background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
              padding: '2px 10px', borderRadius: 20,
              backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
              boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
            }}>{lead.name}</span>
          </div>

          {/* Textarea */}
          <div style={{ padding: '10px 14px 0' }}>
            <textarea
              autoFocus
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Notiz eingeben…"
              rows={5}
              className="gl-input"
              style={{
                width: '100%',
                padding: '10px 12px', fontSize: 13,
                outline: 'none', resize: 'vertical',
                color: 'var(--text)',
                lineHeight: 1.65, fontFamily: 'var(--font)',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {noteSnippets.length > 0 && (
            <div style={{ padding: '6px 14px 0' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text3)', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Schnellvorlagen</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {noteSnippets.map(n => (
                  <button key={n.id} type="button" onClick={() => setText(prev => (prev ? `${prev}\n\n` : '') + (n.text || ''))}
                    style={{ fontSize: 10, fontWeight: 600, padding: '3px 9px', borderRadius: 20, border: '1px solid var(--border)', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)', cursor: 'pointer', color: 'var(--text2)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.7)' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-bg)'; e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.borderColor = 'var(--accent-border)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.color = 'var(--text2)'; e.currentTarget.style.borderColor = 'var(--border)'; }}>
                    {n.branch || n.status || 'Vorlage'}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Formatting toolbar */}
          <div style={{ padding: '6px 14px', display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.2)' }}>
            {[
              { label: 'B', style: { fontWeight: 700 } },
              { label: 'I', style: { fontStyle: 'italic' } },
              { label: 'U', style: { textDecoration: 'underline' } },
              { label: 'S', style: { textDecoration: 'line-through' } },
            ].map(btn => (
              <button key={btn.label} style={{ ...toolbarBtnStyle, ...btn.style }}>{btn.label}</button>
            ))}
            <span style={{ width: 1, height: 16, background: '#d0dce8', margin: '0 4px' }} />
            <button style={toolbarBtnStyle}>Mehr ▾</button>
            <span style={{ width: 1, height: 16, background: '#d0dce8', margin: '0 4px' }} />
            {/* Link icon */}
            <button style={toolbarBtnStyle} title="Link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5e7a68" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
            </button>
            {/* Image icon */}
            <button style={toolbarBtnStyle} title="Bild">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5e7a68" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
            </button>
            {/* Attachment icon */}
            <button style={toolbarBtnStyle} title="Anhang">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5e7a68" strokeWidth="2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
            </button>
            {/* AI sparkle */}
            <button style={{ ...toolbarBtnStyle, marginLeft: 'auto', color: '#159c5f' }} title="KI-Assistent">✦</button>
          </div>

          {/* Datensätze zugeordnet */}
          <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255,255,255,0.4)' }}>
            <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#159c5f', fontWeight: 500, padding: 0 }}>
              1 Datensatz zugeordnet ▾
            </button>
          </div>

          {/* Task checkbox */}
          <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.22)', borderBottom: '1px solid rgba(255,255,255,0.4)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexWrap: 'wrap' }}>
              <input type="checkbox" checked={createTask} onChange={e => setCreateTask(e.target.checked)}
                style={{ accentColor: 'var(--accent)', width: 15, height: 15, flexShrink: 0, cursor: 'pointer' }} />
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>Eine</span>
              <select value={taskType} onChange={e => setTaskType(e.target.value)} style={inlineSelectStyle}>
                <option>To-do</option>
                <option>Anruf</option>
                <option>E-Mail</option>
              </select>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>-Aufgabe als Follow-up für</span>
              <select value={followupIdx} onChange={e => setFollowupIdx(Number(e.target.value))} style={inlineSelectStyle}>
                {FOLLOWUP_OPTIONS.map((o, i) => (
                  <option key={i} value={i}>{o.label} ({getWeekday(o.days)})</option>
                ))}
              </select>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>erstellen</span>
            </label>
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 14px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button onClick={onClose} style={{ height: 34, padding: '0 14px', borderRadius: 8, background: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.6)', color: 'var(--text2)', fontSize: 13, cursor: 'pointer', backdropFilter: 'blur(8px)' }}>
              Abbrechen
            </button>
            <button onClick={handleSave} disabled={!text.trim()} className={text.trim() ? 'gl-btn-primary' : ''} style={{
              height: 34, padding: '0 20px', borderRadius: 8,
              background: text.trim() ? undefined : 'rgba(255,255,255,0.25)',
              border: text.trim() ? undefined : '1px solid rgba(255,255,255,0.4)',
              color: text.trim() ? '#fff' : 'var(--text3)',
              fontSize: 13, fontWeight: 600,
              cursor: text.trim() ? 'pointer' : 'default',
            }}>
              Notiz speichern
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const iconBtnStyle = {
  width: 24, height: 24, borderRadius: 5,
  background: 'rgba(255,255,255,0.08)',
  border: 'none', cursor: 'pointer',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 0,
  transition: 'background .1s',
};

const toolbarBtnStyle = {
  minWidth: 26, height: 26, borderRadius: 5,
  background: 'none', border: 'none',
  cursor: 'pointer', color: 'var(--text2)',
  fontSize: 12, fontWeight: 600,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '0 4px',
  transition: 'background .1s, color .1s',
};

const inlineSelectStyle = {
  fontSize: 12, fontWeight: 600, color: 'var(--accent)',
  background: 'rgba(29,190,116,0.08)', border: '1px solid var(--accent-border)',
  borderRadius: 5, padding: '1px 6px',
  cursor: 'pointer', outline: 'none',
  fontFamily: 'var(--font)',
};
